import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    LinearProgress,
    Avatar,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import {
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Apps as AppsIcon,
    SupportAgent as SupportAgentIcon,
    BugReport as BugReportIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Notifications as NotificationsIcon
} from "@mui/icons-material";

export default function MainPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    // Données simulées - à remplacer par des appels API
    const stats = {
        totalClients: 12,
        totalApplications: 8,
        openTickets: 4,
        resolvedTickets: 23,
        clientsGrowth: +15.2,
        ticketsThisWeek: 7
    };

    const recentActivity = [
        {
            id: 1,
            type: 'client',
            message: 'Nouveau client: Beta Solutions',
            time: '2 heures',
            icon: <PeopleIcon />,
            color: 'success'
        },
        {
            id: 2,
            type: 'ticket',
            message: 'Ticket #4 résolu - Erreur QR Generator',
            time: '4 heures',
            icon: <CheckCircleIcon />,
            color: 'success'
        },
        {
            id: 3,
            type: 'application',
            message: 'Mise à jour Password Manager v1.2.0',
            time: '1 jour',
            icon: <AppsIcon />,
            color: 'info'
        },
        {
            id: 4,
            type: 'ticket',
            message: 'Nouveau ticket critique - Vulnérabilité sécurité',
            time: '2 jours',
            icon: <BugReportIcon />,
            color: 'error'
        }
    ];

    const urgentTasks = [
        {
            id: 1,
            task: 'Corriger la vulnérabilité de sécurité',
            priority: 'Critique',
            dueDate: 'Aujourd\'hui'
        },
        {
            id: 2,
            task: 'Répondre au ticket Beta Solutions',
            priority: 'Haute',
            dueDate: 'Demain'
        },
        {
            id: 3,
            task: 'Déployer ERP Manager v3.3.0',
            priority: 'Moyenne',
            dueDate: 'Cette semaine'
        }
    ];

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critique': return 'error';
            case 'Haute': return 'warning';
            case 'Moyenne': return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 3 }}>
                Dashboard ERP/CRM
            </Typography>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #ca0000 0%, #e5484d 100%)', color: 'white' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Clients
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                        {stats.totalClients}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        <Typography variant="body2">
                                            +{stats.clientsGrowth}% ce mois
                                        </Typography>
                                    </Box>
                                </Box>
                                <PeopleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Applications
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        {stats.totalApplications}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        6 actives, 2 en développement
                                    </Typography>
                                </Box>
                                <AppsIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Tickets Ouverts
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: stats.openTickets > 5 ? 'error.main' : 'primary.main', fontWeight: 'bold' }}>
                                        {stats.openTickets}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        {stats.ticketsThisWeek} cette semaine
                                    </Typography>
                                </Box>
                                <SupportAgentIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Tickets Résolus
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                        {stats.resolvedTickets}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        Ce mois-ci
                                    </Typography>
                                </Box>
                                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Activité récente */}
                <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            Activité Récente
                        </Typography>
                        <List>
                            {recentActivity.map((activity, index) => (
                                <ListItem key={activity.id} divider={index < recentActivity.length - 1}>
                                    <ListItemIcon>
                                        <Avatar sx={{ bgcolor: `${activity.color}.main`, width: 32, height: 32 }}>
                                            {React.cloneElement(activity.icon, { sx: { fontSize: 16 } })}
                                        </Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={activity.message}
                                        secondary={`Il y a ${activity.time}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Tâches urgentes */}
                <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ScheduleIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                Tâches Urgentes
                            </Typography>
                        </Box>
                        <List>
                            {urgentTasks.map((task, index) => (
                                <ListItem key={task.id} sx={{ px: 0, py: 1 }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                                                {task.task}
                                            </Typography>
                                            <Chip
                                                label={task.priority}
                                                color={getPriorityColor(task.priority)}
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Échéance: {task.dueDate}
                                        </Typography>
                                        {task.priority === 'Critique' && (
                                            <LinearProgress
                                                variant="indeterminate"
                                                color="error"
                                                sx={{ mt: 1, height: 2 }}
                                            />
                                        )}
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>

            {/* Performance mensuelle */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            Performance ce mois-ci
                        </Typography>
                        <Grid container spacing={4} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                        95%
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        Satisfaction Client
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={95}
                                        color="success"
                                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        12h
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        Temps moyen de résolution
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={75}
                                        sx={{ mt: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                                        99.2%
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        Disponibilité des services
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={99.2}
                                        color="info"
                                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}